import { useCallback, useState } from 'react';
import type { LanguageValue } from '@elfsight/configurator-contracts';
import type {
  CustomerData,
  PaymentMetadata,
  PaymentProcessingChangeHandler,
  PaymentSuccessHandler,
  ProductData,
  StripeIntegrationSettings
} from '@elfsight/lib-stripe-integration';
import {
  CheckoutLoadingContainer,
  createCheckoutSession,
  PaymentErrorMessage,
  PaymentSessionMetadataProvider,
  Section,
  STYLE_DEFAULTS,
  toStripeAmount,
  usePaymentSessionMetadata,
  useStripe
} from '@elfsight/lib-stripe-integration';
import { Show } from '@elfsight/lib-utils';
import { useLingui } from '@lingui/react/macro';
import { CheckoutProvider } from '@stripe/react-stripe-js';
import { useSettings } from '@wu/widget-core';
import { noop } from 'es-toolkit';
import type { TextsDomain } from '../common';
import { CustomerDataBlock } from './components';
import { WidgetDomainTextsProvider } from './domain-texts';
import { StripeCheckoutForm } from './stripe-checkout-form';
import { usePaymentAppearances } from './utils';

export type PaymentBlockProps = {
  productData: ProductData;
  customerData: CustomerData;
  paymentMetadata?: PaymentMetadata;
  onCustomerDataEdit: () => void;
  onPaymentSuccess: PaymentSuccessHandler;
  textsDomain: TextsDomain;
  onPaymentProcessing?: PaymentProcessingChangeHandler;
  color?: string;
  accentColor?: string;
};

export function StripePaymentBlock({
  textsDomain,
  onPaymentProcessing,
  ...forwardingProps
}: PaymentBlockProps) {
  return (
    <PaymentSessionMetadataProvider onPaymentProcessing={onPaymentProcessing}>
      <WidgetDomainTextsProvider domain={textsDomain}>
        <StripePaymentBlockInner {...forwardingProps} />
      </WidgetDomainTextsProvider>
    </PaymentSessionMetadataProvider>
  );
}

export function StripePaymentBlockInner({
  productData: rawProductData,
  customerData,
  paymentMetadata,
  onCustomerDataEdit,
  onPaymentSuccess = noop,
  color = STYLE_DEFAULTS.textColor,
  accentColor = STYLE_DEFAULTS.accentColor
}: Omit<PaymentBlockProps, 'textsDomain' | 'onPaymentProcessing'>) {
  const { t } = useLingui();
  const {
    integrationStripe: { id: integrationId },
    widgetToken,
    language
  } = useSettings<
    StripeIntegrationSettings & {
      widgetToken?: string;
      language: LanguageValue;
    }
  >();
  const { language: locale = window.navigator.language } = language ?? {};

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const { setSessionId, isPaymentInProcess } = usePaymentSessionMetadata();
  const { appearance, disabledAppearance } = usePaymentAppearances({
    color,
    accentColor
  });

  const stripe = useStripe(() => setError(t`Unable to load Stripe`));

  const fetchClientSecret = useCallback(async () => {
    const unableToCreateSessionError = t`We’re sorry, the payment page isn’t available right now. Please contact us for assistance.`;

    const convertedProductsData: ProductData = {
      ...rawProductData,
      amount: toStripeAmount(rawProductData)
    };

    try {
      const { clientSecret, sessionId } = await createCheckoutSession({
        providerIntegrationId: integrationId,
        customerEmail: customerData.email,
        productData: convertedProductsData,
        paymentMetadata,
        widgetToken,
        locale
      });

      if (clientSecret && sessionId) {
        setSessionId(sessionId);

        return clientSecret;
      }

      setError(unableToCreateSessionError);

      return '';
    } catch (_) {
      setError(unableToCreateSessionError);

      return '';
    }
  }, [
    integrationId,
    rawProductData,
    customerData.email,
    paymentMetadata,
    widgetToken,
    setSessionId,
    t
  ]);

  return (
    <CheckoutLoadingContainer
      isLoading={isLoading && !error}
      textColor={color}
      detailsWithName={!!customerData.name}
    >
      <CustomerDataBlock
        onEdit={onCustomerDataEdit}
        color={color}
        actionColor={accentColor}
        email={customerData.email}
        name={customerData.name}
        isActionDisabled={isPaymentInProcess}
      />

      <CheckoutProvider
        stripe={stripe}
        options={{
          fetchClientSecret,
          elementsOptions: {
            appearance: isPaymentInProcess ? disabledAppearance : appearance,
            savedPaymentMethod: {
              enableSave: 'auto',
              enableRedisplay: 'auto'
            },
            loader: 'always'
          }
        }}
      >
        <StripeCheckoutForm
          onPaymentSuccess={onPaymentSuccess}
          color={color}
          accentColor={accentColor}
          onReady={() => setIsLoading(false)}
        />
      </CheckoutProvider>

      <Show when={!!error}>
        <Section>
          <PaymentErrorMessage>{error}</PaymentErrorMessage>
        </Section>
      </Show>
    </CheckoutLoadingContainer>
  );
}
